import { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  fetchCandidato,
  fmtVotos,
  fmtPct,
  fmtFecha,
  fmtBytes,
  edadEnAnios,
  type Integrante,
  type PlanGobierno,
} from "@/lib/candidatos";

export function CandidatoFichaPage() {
  const { slug = "" } = useParams<{ slug: string }>();
  const [modalIntegrante, setModalIntegrante] = useState<Integrante | null>(null);
  const { data, isLoading, error } = useQuery({
    queryKey: ["candidato", slug],
    queryFn: () => fetchCandidato(slug),
    enabled: !!slug,
  });
  useEffect(() => {
    setModalIntegrante(null);
  }, [slug]);

  if (isLoading) {
    return (
      <div className="container-prose py-16">
        <span className="chip-vacio">Cargando ficha…</span>
      </div>
    );
  }
  if (error || !data) {
    return (
      <div className="container-prose py-16">
        <span className="chip-contradiccion">No se encontró el candidato: {slug}</span>
        <p className="mt-4 text-ink-200">
          <Link to="/candidatos" className="text-gold-300 hover:underline">
            ← Ver universo completo (38 filas)
          </Link>
        </p>
      </div>
    );
  }

  const { candidato, plancha, eventos, contexto, proceso } = data;
  const isVacio = candidato.tipo !== "candidato";
  const presidente = plancha?.integrantes.find((i) => i.posicion === 1) ?? null;
  const vp1 = plancha?.integrantes.find((i) => i.posicion === 2) ?? null;
  const vp2 = plancha?.integrantes.find((i) => i.posicion === 3) ?? null;

  return (
    <div className="container-prose py-12 md:py-16">
      <div className="mb-6">
        <Link to="/candidatos" className="text-sm text-ink-300 hover:text-gold-300">
          ← Volver al universo completo
        </Link>
      </div>

      {/* Hero con foto + logo + nombre + organización */}
      <div className="flex flex-wrap items-start gap-6">
        {presidente?.fotoUrl ? (
          <img
            src={presidente.fotoUrl}
            alt={candidato.nombreCandidato}
            className="h-32 w-32 rounded-xl object-cover ring-2 ring-gold-500/40 shadow-xl shadow-ink-950/50"
          />
        ) : candidato.iniciales ? (
          <div className="grid h-32 w-32 place-items-center rounded-xl bg-gold-500/10 ring-1 ring-gold-500/40 text-gold-300 font-serif text-4xl">
            {candidato.iniciales}
          </div>
        ) : (
          <div className="grid h-32 w-32 place-items-center rounded-xl bg-ink-800 ring-1 ring-ink-700 text-ink-400 text-3xl">
            —
          </div>
        )}
        <div className="flex-1 min-w-[280px]">
          <div className="flex flex-wrap items-center gap-2">
            <span className="chip-reconocido">🎯 Presidencial EG2026</span>
            <span className="chip-probado">🟢 ONPE + JNE</span>
            {isVacio && <span className="chip-vacio">No es candidatura</span>}
            {presidente?.estadoCandidato && presidente.estadoCandidato !== "INSCRITO" && (
              <span className="chip-contradiccion">{presidente.estadoCandidato}</span>
            )}
          </div>
          <h1 className="mt-3 font-serif text-3xl text-ink-50 md:text-4xl leading-tight">
            {candidato.nombreCandidato}
          </h1>
          <div className="mt-2 flex flex-wrap items-center gap-3">
            {plancha?.logoUrl && (
              <img
                src={plancha.logoUrl}
                alt={plancha.nombreOrganizacion}
                className="h-10 w-10 rounded object-contain"
              />
            )}
            <div className="text-ink-200">{candidato.nombreOrganizacion}</div>
          </div>
          {candidato.dniCandidato && (
            <div className="mt-1 text-xs text-ink-400 font-mono">DNI {candidato.dniCandidato}</div>
          )}
        </div>
      </div>

      {/* Resultado oficial */}
      <section className="mt-10">
        <h2 className="text-xs uppercase tracking-widest text-ink-400">Resultado oficial ONPE</h2>
        <div className="mt-3 grid gap-3 md:grid-cols-4">
          <FichaCard label="Votos absolutos" value={fmtVotos(candidato.votosAbsolutos)} />
          <FichaCard
            label="% sobre válidos"
            value={isVacio ? "—" : fmtPct(candidato.pctValidos)}
            hint="denominador canónico"
            highlight
          />
          <FichaCard
            label="% sobre emitidos"
            value={fmtPct(candidato.pctEmitidos)}
            hint="incluye blancos+nulos"
          />
          <FichaCard
            label="Ranking oficial"
            value={`#${candidato.ranking}`}
            hint="de 38 filas (36 candidatos + blanco + nulo)"
          />
        </div>
      </section>

      {/* Plancha completa */}
      {plancha && (
        <section className="mt-10">
          <div className="flex items-end justify-between gap-3">
            <h2 className="text-xs uppercase tracking-widest text-ink-400">
              Plancha presidencial — Voto Informado JNE
            </h2>
            {(plancha.planes ?? []).length > 0 && (
              <a
                href="#plan-gobierno"
                className="text-xs text-gold-300 hover:underline"
              >
                Plan de Gobierno ↓
              </a>
            )}
          </div>
          <div className="mt-3 rounded-lg border border-ink-700/60 bg-ink-900/40 p-5">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <div className="font-serif text-lg text-ink-50">{plancha.nombreOrganizacion}</div>
                <div className="text-xs text-ink-400">
                  {plancha.tipoOrgPolitica} · Expediente{" "}
                  <code className="text-ink-200">{plancha.codigoExpediente}</code> · JEE{" "}
                  {plancha.jeeCreacion}
                </div>
              </div>
              <a
                href={`https://votoinformado.jne.gob.pe/elecciones-generales`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-gold-300 hover:underline"
              >
                Ver en Voto Informado JNE →
              </a>
            </div>
            <div className="mt-5 grid gap-4 md:grid-cols-3">
              {[presidente, vp1, vp2].filter(Boolean).map((i) => (
                <IntegranteCard
                  key={i!.posicion}
                  integrante={i!}
                  onClick={() => setModalIntegrante(i!)}
                />
              ))}
            </div>
            <p className="mt-3 text-xs text-ink-400">
              Click en cualquiera de las cards para ver la hoja de Voto Informado JNE.
            </p>
          </div>
        </section>
      )}

      {/* Plan de Gobierno */}
      {plancha && (
        <section id="plan-gobierno" className="mt-10 scroll-mt-20">
          <h2 className="text-xs uppercase tracking-widest text-ink-400">
            Plan de Gobierno 2026-2031 — entregado al JNE
          </h2>
          <PlanesPanel planes={plancha.planes ?? []} />
        </section>
      )}

      {/* Trazabilidad */}
      <section className="mt-8 rounded-lg border border-ink-700/60 bg-ink-900/40 p-5">
        <h2 className="text-xs uppercase tracking-widest text-ink-400">Trazabilidad de las cifras</h2>
        <div className="mt-3 grid gap-2 text-sm md:grid-cols-2">
          <div>
            <span className="text-ink-300">Snapshot ONPE:</span>{" "}
            <span className="text-ink-100 font-mono">{fmtFecha(candidato.snapshotAt)}</span>
          </div>
          {proceso && (
            <div>
              <span className="text-ink-300">Actas contabilizadas:</span>{" "}
              <span className="text-ink-100 font-mono">{fmtPct(proceso.pctContabilizadas, 2)}</span>
            </div>
          )}
          <div className="md:col-span-2">
            <span className="text-ink-300">Fuente votos:</span>{" "}
            <code className="text-ink-200 text-xs">{candidato.fuente}</code>
          </div>
          {plancha && (
            <div className="md:col-span-2">
              <span className="text-ink-300">Fuente plancha + fotos:</span>{" "}
              <code className="text-ink-200 text-xs">
                votoinformado.jne.gob.pe — snapshot {fmtFecha(plancha.fetchedAt)}
              </code>
            </div>
          )}
        </div>
      </section>

      {/* Contexto delta */}
      <section className="mt-8">
        <h2 className="text-xs uppercase tracking-widest text-ink-400">Contexto en el ranking</h2>
        <div className="mt-3 grid gap-3 md:grid-cols-2">
          {contexto.anterior ? (
            <DeltaCard
              titulo={`Sobre ${contexto.anterior.nombreCandidato} (#${contexto.anterior.ranking})`}
              delta={-(contexto.deltaConAnterior ?? 0)}
              link={`/candidatos/${contexto.anterior.slug}`}
              tone="atras"
            />
          ) : (
            <div className="card-ink text-sm text-ink-400">Es el primero en el ranking.</div>
          )}
          {contexto.siguiente ? (
            <DeltaCard
              titulo={`Sobre ${contexto.siguiente.nombreCandidato} (#${contexto.siguiente.ranking})`}
              delta={contexto.deltaConSiguiente ?? 0}
              link={`/candidatos/${contexto.siguiente.slug}`}
              tone="adelante"
            />
          ) : (
            <div className="card-ink text-sm text-ink-400">Es el último en el ranking.</div>
          )}
        </div>
      </section>

      {/* Timeline eventos */}
      <section className="mt-10">
        <h2 className="text-xl font-serif text-ink-50">¿Qué pasó con esta candidatura?</h2>
        <p className="mt-1 text-sm text-ink-300">
          Tachas, resoluciones JEE/JNE, denuncias por/contra, declaraciones. Se nutre del sync JNE
          (bloque F).
        </p>
        {eventos.length === 0 ? (
          <div className="mt-4 rounded-lg border border-ink-700/40 bg-ink-900/40 p-6">
            <span className="chip-probado">🟢 Sin observaciones registradas</span>
            <p className="mt-3 text-sm text-ink-300">
              Al cierre del snapshot, no hay tachas, observaciones ni resoluciones JEE/JNE
              asociadas en el expediente del sitio. Estado JNE actual:{" "}
              <strong className="text-ink-100">
                {presidente?.estadoCandidato ?? "INSCRITO"}
              </strong>
              .
            </p>
          </div>
        ) : (
          <ol className="mt-4 space-y-3">
            {eventos.map((e) => (
              <li key={e.id} className="rounded-lg border border-ink-700/60 bg-ink-900/40 p-4">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="text-xs uppercase tracking-widest text-gold-400/80">{e.tipo}</span>
                  <time className="text-xs text-ink-400 font-mono">{fmtFecha(e.fecha)}</time>
                </div>
                <h3 className="mt-2 font-serif text-lg text-ink-50">{e.titulo}</h3>
                <p className="mt-1 text-sm text-ink-200">{e.resumen}</p>
              </li>
            ))}
          </ol>
        )}
      </section>

      {modalIntegrante && (
        <IntegranteModal
          integrante={modalIntegrante}
          onClose={() => setModalIntegrante(null)}
        />
      )}
    </div>
  );
}

function IntegranteCard({
  integrante: i,
  onClick,
}: {
  integrante: Integrante;
  onClick?: () => void;
}) {
  const edad = i.fechaNacimiento ? edadEnAnios(i.fechaNacimiento) : null;
  const colorByPos = i.posicion === 1 ? "border-gold-500/40" : "border-ink-700/60";
  const label =
    i.posicion === 1 ? "Presidente" : i.posicion === 2 ? "1° Vicepresidente" : "2° Vicepresidente";
  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full text-left rounded-lg border bg-ink-950/40 p-4 transition hover:border-gold-500/60 hover:bg-ink-900/60 cursor-pointer ${colorByPos}`}
    >
      <div className="flex items-center justify-between">
        <div className="text-xs uppercase tracking-widest text-gold-400/80">{label}</div>
        <span className="text-xs text-ink-400 group-hover:text-gold-300">ver hoja →</span>
      </div>
      <div className="mt-3 flex items-start gap-3">
        {i.fotoUrl ? (
          <img
            src={i.fotoUrl}
            alt={i.nombreCompleto}
            className="h-20 w-20 rounded-md object-cover ring-1 ring-ink-700/60"
          />
        ) : (
          <div className="h-20 w-20 rounded-md bg-ink-800 grid place-items-center text-ink-400 text-xs">
            sin foto
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="font-serif text-base text-ink-50 leading-tight">{i.nombreCompleto}</div>
          <div className="mt-1 text-xs text-ink-300 space-y-0.5">
            <div>DNI {i.dni}</div>
            <div>
              {i.sexo}
              {edad !== null && ` · ${edad} años`}
            </div>
            {i.esNativo === "SI" && <div className="text-gold-300">Originario</div>}
            {i.estadoCandidato && i.estadoCandidato !== "INSCRITO" && (
              <div className="text-sem-contradiccion">{i.estadoCandidato}</div>
            )}
          </div>
        </div>
      </div>
    </button>
  );
}

function IntegranteModal({
  integrante: i,
  onClose,
}: {
  integrante: Integrante;
  onClose: () => void;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  const edad = i.fechaNacimiento ? edadEnAnios(i.fechaNacimiento) : null;
  const cargoCorto =
    i.posicion === 1 ? "Presidente" : i.posicion === 2 ? "1° Vicepresidente" : "2° Vicepresidente";

  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center bg-ink-950/85 p-4 backdrop-blur"
      onClick={onClose}
    >
      <div
        className="w-full max-w-2xl rounded-xl border border-gold-500/30 bg-ink-900 shadow-2xl shadow-ink-950/80"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4 border-b border-ink-700/60 px-6 py-4">
          <div>
            <div className="text-xs uppercase tracking-widest text-gold-400/80">
              {cargoCorto} · Voto Informado JNE
            </div>
            <div className="mt-1 text-xs text-ink-400">{i.cargo}</div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-ink-300 hover:text-gold-200 text-2xl leading-none"
            aria-label="Cerrar"
          >
            ×
          </button>
        </div>

        <div className="grid gap-6 px-6 py-6 md:grid-cols-[200px_1fr]">
          <div>
            {i.fotoUrl ? (
              <img
                src={i.fotoUrl}
                alt={i.nombreCompleto}
                className="w-full rounded-lg object-cover ring-1 ring-gold-500/40 shadow-lg shadow-ink-950/50"
              />
            ) : (
              <div className="grid h-48 w-full place-items-center rounded-lg bg-ink-800 text-ink-400 text-sm">
                sin foto
              </div>
            )}
          </div>
          <div className="space-y-3">
            <div>
              <h3 className="font-serif text-2xl text-ink-50 leading-tight">
                {i.nombreCompleto}
              </h3>
              <div className="mt-1 text-xs text-ink-400">
                {i.nombres} · {i.apellidoPaterno} {i.apellidoMaterno ?? ""}
              </div>
            </div>

            <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
              <Dt label="Documento">
                {i.tipoDocumento} {i.dni}
              </Dt>
              <Dt label="Sexo">{i.sexo}</Dt>
              <Dt label="Fecha de nacimiento">
                {i.fechaNacimiento ? fmtFecha(i.fechaNacimiento) : "—"}
                {edad !== null && (
                  <span className="text-ink-400"> ({edad} años)</span>
                )}
              </Dt>
              <Dt label="¿Es originario?">
                {i.esNativo === "SI" ? (
                  <span className="text-gold-300">Sí, comunidad nativa/originaria</span>
                ) : (
                  <span className="text-ink-200">No</span>
                )}
              </Dt>
              <Dt label="Estado JNE">
                <span
                  className={
                    i.estadoCandidato && i.estadoCandidato !== "INSCRITO"
                      ? "text-sem-contradiccion"
                      : "text-sem-probado"
                  }
                >
                  {i.estadoCandidato ?? "—"}
                </span>
              </Dt>
            </dl>

            <div className="mt-4 rounded-lg border border-ink-700/40 bg-ink-950/40 p-3 text-xs text-ink-400">
              <strong className="text-ink-200">Hoja de vida completa</strong> (bienes, formación,
              experiencia laboral, antecedentes judiciales): pendiente en el bloque F del proyecto
              (sync HVConsolidado JNE con browser real). Mientras, consulta el portal oficial:
              <div className="mt-2">
                <a
                  href="https://votoinformado.jne.gob.pe/elecciones-generales"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-gold-300 hover:underline"
                >
                  Buscar {i.apellidoPaterno} en Voto Informado JNE →
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Dt({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <dt className="text-xs uppercase tracking-widest text-ink-400">{label}</dt>
      <dd className="mt-0.5 text-ink-100">{children}</dd>
    </div>
  );
}

function FichaCard({
  label,
  value,
  hint,
  highlight,
}: {
  label: string;
  value: string;
  hint?: string;
  highlight?: boolean;
}) {
  return (
    <div
      className={`rounded-lg border p-4 ${
        highlight ? "border-gold-500/40 bg-gold-500/5" : "border-ink-700/60 bg-ink-900/60"
      }`}
    >
      <div className="text-xs uppercase tracking-widest text-ink-400">{label}</div>
      <div
        className={`mt-1 font-mono text-2xl tabular-nums ${
          highlight ? "text-gold-200" : "text-ink-50"
        }`}
      >
        {value}
      </div>
      {hint && <div className="mt-1 text-xs text-ink-400">{hint}</div>}
    </div>
  );
}

function PlanesPanel({ planes }: { planes: PlanGobierno[] }) {
  if (planes.length === 0) {
    return (
      <div className="mt-3 rounded-lg border border-ink-700/60 bg-ink-900/40 p-5">
        <span className="chip-vacio">⚫ Vacío documental</span>
        <p className="mt-2 text-sm text-ink-300">
          Esta organización política no figura en el archivo de Planes de Gobierno del JNE para
          EG2026. Si entregó plan posteriormente, se incorporará en el próximo sync.
        </p>
      </div>
    );
  }

  const pg = planes.find((p) => p.tipo === "PG");
  const resumen = planes.find((p) => p.tipo === "RESUMIDO");
  const vacio = planes.find((p) => p.tipo === "VACIO");

  if (vacio && !pg && !resumen) {
    return (
      <div className="mt-3 rounded-lg border border-sem-vacio/40 bg-ink-900/40 p-5">
        <span className="chip-vacio">⚫ Marcado VACÍO por JNE</span>
        <p className="mt-2 text-sm text-ink-300">
          La carpeta oficial del JNE marca explícitamente que esta organización no entregó plan
          de gobierno completo ni resumen.
        </p>
      </div>
    );
  }

  return (
    <div className="mt-3 grid gap-3 md:grid-cols-2">
      {pg && (
        <div className="rounded-lg border border-gold-500/40 bg-ink-900/40 p-5">
          <div className="flex items-center justify-between">
            <span className="chip-probado">📄 Plan completo</span>
            <span className="text-xs text-ink-400">{fmtBytes(pg.bytes)}</span>
          </div>
          <h3 className="mt-2 font-serif text-lg text-ink-50">Plan de Gobierno 2026-2031</h3>
          <p className="mt-1 text-xs text-ink-400">
            Documento entregado por la organización política al JNE en febrero 2026.
          </p>
          {pg.urlLocal && (
            <div className="mt-3 flex flex-wrap gap-2">
              <a
                href={pg.urlLocal}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 rounded-md bg-gold-500/20 px-3 py-1.5 text-sm font-medium text-gold-200 hover:bg-gold-500/30 transition"
              >
                Ver PDF →
              </a>
              <a
                href={pg.urlLocal}
                download
                className="inline-flex items-center gap-1.5 rounded-md border border-ink-700 px-3 py-1.5 text-sm text-ink-200 hover:border-gold-500/40 hover:text-gold-200 transition"
              >
                Descargar
              </a>
              {pg.urlOriginal && (
                <a
                  href={pg.urlOriginal}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center text-xs text-ink-400 hover:text-gold-300 transition self-center"
                  title="Fuente oficial JNE"
                >
                  fuente original ↗
                </a>
              )}
            </div>
          )}
        </div>
      )}
      {resumen && (
        <div className="rounded-lg border border-ink-700/60 bg-ink-900/40 p-5">
          <div className="flex items-center justify-between">
            <span className="chip-reconocido">📑 Resumen ejecutivo</span>
            <span className="text-xs text-ink-400">{fmtBytes(resumen.bytes)}</span>
          </div>
          <h3 className="mt-2 font-serif text-lg text-ink-50">Plan resumido</h3>
          <p className="mt-1 text-xs text-ink-400">
            Versión resumida (formato JNE) — ideal para lectura rápida.
          </p>
          {resumen.urlLocal && (
            <div className="mt-3 flex flex-wrap gap-2">
              <a
                href={resumen.urlLocal}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 rounded-md bg-ink-800 px-3 py-1.5 text-sm font-medium text-ink-100 hover:bg-ink-700 transition"
              >
                Ver resumen →
              </a>
              <a
                href={resumen.urlLocal}
                download
                className="inline-flex items-center gap-1.5 rounded-md border border-ink-700 px-3 py-1.5 text-sm text-ink-200 hover:border-gold-500/40 hover:text-gold-200 transition"
              >
                Descargar
              </a>
            </div>
          )}
        </div>
      )}
      {!pg && resumen && (
        <div className="rounded-lg border border-ink-700/40 bg-ink-900/30 p-4 text-xs text-ink-400 md:col-span-2">
          ⚫ Sólo resumen disponible — el plan de gobierno completo no figura en el archivo del JNE.
        </div>
      )}
      {pg && !resumen && (
        <div className="rounded-lg border border-ink-700/40 bg-ink-900/30 p-4 text-xs text-ink-400 md:col-span-2">
          ⚫ Sólo plan completo disponible — el resumen ejecutivo no figura en el archivo del JNE.
        </div>
      )}
    </div>
  );
}

function DeltaCard({
  titulo,
  delta,
  link,
  tone,
}: {
  titulo: string;
  delta: number;
  link: string;
  tone: "atras" | "adelante";
}) {
  const sign = delta > 0 ? "+" : "";
  return (
    <Link
      to={link}
      className="block rounded-lg border border-ink-700/60 bg-ink-900/40 p-4 transition hover:border-gold-500/40"
    >
      <div className="text-xs uppercase tracking-widest text-ink-400">
        {tone === "atras" ? "Δ vs el siguiente arriba" : "Δ vs el siguiente abajo"}
      </div>
      <div className="mt-1 font-mono text-2xl tabular-nums text-ink-50">
        {sign}
        {fmtVotos(delta)} votos
      </div>
      <div className="mt-1 text-xs text-ink-300">{titulo}</div>
    </Link>
  );
}
